import { createSelector } from "@reduxjs/toolkit";
import { getDeeplyNestedValue } from "@multiconsult-group/multiplacer-core";
import {
  selectHttpMcBimItemsInStandardSpace,
} from "./httpMcBimStandardSelectors";
import { inferPrimarySystemKindFromPrimarySystemName } from "../systemsConstants";
import type { BuildRoutingGraphFromMcBimPipeGeometriesArgs } from "@multiconsult-group/multiplacer-core";
import { selectCompCodePath } from "../globalSelectors";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";

type IRoutingPipeWithMcBimGeometry =
  BuildRoutingGraphFromMcBimPipeGeometriesArgs["mcBimPipeGeometries"][number];

export type IRoutingPipeGeometriesFromStandardSpace = {
  system: string;
  roomNumber?: string;
  mcBimPipeGeometries: IRoutingPipeWithMcBimGeometry[];
  mcBimConnectionGeometries: IRoutingPipeWithMcBimGeometry[];
};

const ALLOWED_PRIMARY_SYSTEM_KINDS = new Set([
  "SUPPLY_AIR",
  "EXHAUST_AIR",
  "FIRE_PROTECTION",
  "COOLING",
]);

export const selectHttpRoutingPipeGeometriesFromStandardSpace = createSelector(
  [selectHttpMcBimItemsInStandardSpace, selectCompCodePath, selectHttpSingleRoomFull],
  (items, compCodePath, activeRoom): IRoutingPipeGeometriesFromStandardSpace[] => {
    const pipesWithGeometryBySystemAndGlobalId = new Map<string, IRoutingPipeWithMcBimGeometry>();
    const connectionsWithGeometryBySystemAndGlobalId = new Map<
      string,
      IRoutingPipeWithMcBimGeometry
    >();

    for (const item of items) {
      const instanceClass = item.instanceClass;
      const isPipeOrDuctSegment = instanceClass === "IfcPipeSegment" || instanceClass === "IfcDuctSegment";
      const isPipeOrDuctConnection = instanceClass === "IfcPipeFitting" || instanceClass === "IfcDuctFitting";
      if (!isPipeOrDuctSegment && !isPipeOrDuctConnection) continue;

      const systemName = item?.primarySystem?.name ?? (item as any)?.system;
      if (typeof systemName !== "string") continue;

      const trimmed = systemName.trim();
      if (!trimmed) continue;
      const kind = inferPrimarySystemKindFromPrimarySystemName(trimmed);
      if (!ALLOWED_PRIMARY_SYSTEM_KINDS.has(kind)) continue;

      if (!item.geometry) continue;

      const resolvedCompCode =
        (compCodePath ? getDeeplyNestedValue(item, compCodePath) : undefined) ??
        item.instanceClass ??
        "UNKNOWN";

      const uniqueKey = `${trimmed}::${item.globalId}`;
      const value = {
        system: trimmed,
        compCode: resolvedCompCode,
        // TODO: Resolve real dimension from McBim source fields/psets.
        dimension: 0,
        geometry: item.geometry,
      };

      if (isPipeOrDuctSegment) {
        pipesWithGeometryBySystemAndGlobalId.set(uniqueKey, value);
      } else {
        connectionsWithGeometryBySystemAndGlobalId.set(uniqueKey, value);
      }
    }

    const groupedBySystem = new Map<
      string,
      {
        mcBimPipeGeometries: IRoutingPipeWithMcBimGeometry[];
        mcBimConnectionGeometries: IRoutingPipeWithMcBimGeometry[];
      }
    >();

    for (const pipe of pipesWithGeometryBySystemAndGlobalId.values()) {
      const existing = groupedBySystem.get(pipe.system) ?? {
        mcBimPipeGeometries: [],
        mcBimConnectionGeometries: [],
      };
      existing.mcBimPipeGeometries.push(pipe);
      groupedBySystem.set(pipe.system, existing);
    }

    for (const connection of connectionsWithGeometryBySystemAndGlobalId.values()) {
      const existing = groupedBySystem.get(connection.system) ?? {
        mcBimPipeGeometries: [],
        mcBimConnectionGeometries: [],
      };
      existing.mcBimConnectionGeometries.push(connection);
      groupedBySystem.set(connection.system, existing);
    }

    return Array.from(groupedBySystem.entries())
      .map(([system, grouped]) => ({
        system,
        // Active instance room (not standard room)
        roomNumber: activeRoom?.roomNumber,
        mcBimPipeGeometries: [...grouped.mcBimPipeGeometries].sort((a, b) =>
          a.compCode.localeCompare(b.compCode)
        ),
        mcBimConnectionGeometries: [...grouped.mcBimConnectionGeometries].sort((a, b) =>
          a.compCode.localeCompare(b.compCode)
        ),
      }))
      .sort((a, b) => a.system.localeCompare(b.system));
  }
);

export const selectHttpUniquePipeAndDuctSystemNamesInStandardSpace = createSelector(
  [selectHttpRoutingPipeGeometriesFromStandardSpace],
  (routingInputs): string[] => routingInputs.map((routingInput) => routingInput.system)
);
